import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TipTapEditor } from '@/components/editor/TipTapEditor';
import { generateExcerpt } from '@/lib/tiptap';
import { useCategories } from '@/hooks/useKBData';
import { useAuth } from '@/providers/AuthProvider';
import { cn } from '@/lib/utils';

// Safe JSON parser that never throws and rejects HTML
function safeJsonParse<T = any>(value: unknown): T | null {
  if (typeof value !== "string") return null;
  const s = value.trim();
  if (!s || s.startsWith("<")) return null; // treat HTML as not-json
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

interface KBEditorProps {
  article?: any;
  onSave: (data: any) => Promise<void>;
  onCancel: () => void;
  isSaving?: boolean;
}

export function KBEditor({ article, onSave, onCancel, isSaving = false }: KBEditorProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState(article?.title || '');
  const [slug, setSlug] = useState(article?.slug || '');
  const [categoryId, setCategoryId] = useState<string>(article?.category_id?.toString() || '');

  // Initialize content with safe JSON parsing
  const initialJson = safeJsonParse(article?.content_json);
  const [content, setContent] = useState<{ html: string; json: any | null }>({
    html: article?.content ?? "",
    json: initialJson,
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch categories for dropdown
  const { data: categoriesData } = useCategories();
  const categories = categoriesData?.categories || [];

  // Handle content changes from TipTap editor
  const handleContentUpdate = (payload: { html: string; json: any }) => {
    setContent({ html: payload.html, json: payload.json });
    setHasChanges(true);
  };

  // Auto-generate slug from title
  useEffect(() => {
    if (!article && title) {
      const generatedSlug = title
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setSlug(generatedSlug);
    }
  }, [title, article]);

  // Track changes for any form input
  useEffect(() => {
    if (article) {
      const titleChanged = title !== article.title;
      const slugChanged = slug !== article.slug;
      const categoryChanged = categoryId !== article.category_id?.toString();

      // Safe comparison of content using safeJsonParse
      const originalJson = safeJsonParse(article?.content_json);
      let contentChanged = false;

      if (originalJson && content.json) {
        // Compare JSON objects
        contentChanged = JSON.stringify(content.json) !== JSON.stringify(originalJson);
      } else {
        // Fall back to HTML comparison
        const originalHtml = article?.content || '';
        contentChanged = content.html !== originalHtml;
      }

      setHasChanges(titleChanged || slugChanged || categoryChanged || contentChanged);
    } else {
      setHasChanges(
        title.trim() !== '' ||
        slug.trim() !== '' ||
        categoryId !== '' ||
        content.html.trim() !== ''
      );
    }
  }, [title, slug, categoryId, content, article]);

  const handleSave = async () => {
    console.log('💾 Save button clicked - mobile debug', {
      isMobile: window.innerWidth < 768,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
    if (!content.html || !title.trim() || !slug.trim() || !categoryId || isNaN(parseInt(categoryId)) || !user?.id) {
      const validation = {
        hasContent: !!content.html,
        hasTitle: !!title.trim(),
        hasSlug: !!slug.trim(),
        hasCategoryId: !!categoryId,
        categoryIdValid: !isNaN(parseInt(categoryId)),
        hasUserId: !!user?.id
      };
      console.error('Save validation failed:', validation);

      // Provide user feedback for mobile debugging
      const missingFields = [];
      if (!content.html) missingFields.push('content');
      if (!title.trim()) missingFields.push('title');
      if (!slug.trim()) missingFields.push('slug');
      if (!categoryId) missingFields.push('category');
      if (!user?.id) missingFields.push('user authentication');

      alert(`Cannot save: Missing ${missingFields.join(', ')}`);
      return;
    }

    // Generate excerpt from HTML content
    const excerpt = generateExcerpt(content.html);

    const saveData = {
      title: title.trim(),
      slug: slug.trim(),
      category_id: parseInt(categoryId),
      author_id: user.id,
      content: content.html,
      content_json: content.json ? JSON.stringify(content.json) : null,
      content_format: 'tiptap',
      excerpt,
    };

    try {
      await onSave(saveData);
      setHasChanges(false);
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
      if (!confirmed) return;
    }
    onCancel();
  };

  const canSave = title.trim() && slug.trim() && categoryId && user?.id && content.html.trim() && !isSaving;

  return (
    <div className="flex flex-col h-full">
      {/* Sticky Top Bar */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center space-x-2">
            <h1 className="text-lg font-semibold">
              {article ? 'Edit Article' : 'New Article'}
            </h1>
            {hasChanges && !isSaving && (
              <span className="text-xs text-muted-foreground bg-orange-500/10 text-orange-600 px-2 py-1 rounded">
                Unsaved changes
              </span>
            )}
            {isSaving && (
              <span className="text-xs text-muted-foreground bg-blue-500/10 text-blue-600 px-2 py-1 rounded flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving...
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCancel}
              disabled={isSaving}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              onTouchStart={(e) => {
                // Prevent double-tap zoom on mobile
                e.preventDefault();
              }}
              disabled={!canSave}
              className="touch-manipulation"
            >
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Editor Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
          {/* Article Metadata */}
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Article title..."
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id.toString()}>
                      {category.icon} {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="slug">URL Slug</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="url-friendly-slug"
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                This will be the URL path for your article
              </p>
            </div>
          </div>

          {/* TipTap Editor */}
          <div>
            <Label>Content</Label>
            <div className="mt-1">
              <TipTapEditor
                content={content.json ?? content.html}
                onUpdate={handleContentUpdate}
                placeholder="Start writing your article..."
                className="focus-within:ring-2 focus-within:ring-ring"
                articleTitle={title || 'article'}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Type @ to mention users, use # for headings, - for lists, and [ ] for checklists
            </p>
          </div>

          {/* Mobile Bottom Actions */}
          <div className="md:hidden sticky bottom-4 flex justify-center">
            <div className="bg-background border border-border rounded-lg shadow-lg p-2 flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                onTouchStart={(e) => {
                  // Prevent double-tap zoom on mobile
                  e.preventDefault();
                }}
                disabled={!canSave}
                className="touch-manipulation"
              >
                {isSaving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}